import { Component, Input } from '@angular/core';
import { showErrorSwal } from '@asamco/utils';
import Swal from 'sweetalert2';
import { SettingTemplateArticle, SettingTemplateService } from '../../../services/setting-template.service';
import { DxiColumnModule } from 'devextreme-angular/ui/nested';
import { DxDataGridModule } from 'devextreme-angular';
import { AsyncPipe, NgIf } from '@angular/common';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

type UiSettingTemplateArticle = SettingTemplateArticle & {
  freshdeskArticleIdsText: string; // UI-only field for the grid
};

@Component({
  selector: 'app-link-edit-settingtemplate-article-admin',
  standalone: true,
  imports: [DxiColumnModule, DxDataGridModule, NgIf, AsyncPipe],
  templateUrl: './link-edit-settingtemplate-article-admin.component.html',
  styleUrls: ['./link-edit-settingtemplate-article-admin.component.scss']
})
export class LinkEditSettingtemplateArticleAdminComponent {

  @Input() linkId: number | null = null;

  /** Modal visibility */
  public visible$ = false;

  /** Grid data as Observable */
  public articles$: Observable<UiSettingTemplateArticle[]> = of([]);

  constructor(private service: SettingTemplateService) {}

  /** Open modal */
  public open(linkId: number) {
    this.linkId = linkId;
    this.visible$ = true;
    this.loadArticles();
  }

  /** Close modal */
  public close() {
    this.visible$ = false;
  }

  /** Load articles for the given linkId */
  private loadArticles() {
    if (!this.linkId) return;

    this.articles$ = this.service.getArticlesByLinkId(this.linkId).pipe(
      map(rows => (rows ?? []).map(r => ({
        ...r,
        freshdeskArticleIdsText: (r.freshdeskArticleIds ?? []).join(', ')
      }))),
      catchError(err => {
        showErrorSwal(err);
        return of([]);
      })
    );
  }

  /** Parse comma/space separated IDs -> unique positive numbers */
  private parseIds(input: string): number[] {
    return Array.from(
      new Set(
        (input ?? '')
          .split(/[\s,;]+/g)
          .map(s => s.trim())
          .filter(Boolean)
          .map(s => Number(s))
          .filter(n => Number.isFinite(n) && n > 0)
      )
    );
  }

  /** Save inline edits */
  onRowUpdated(e: any) {
    const text = (e?.data?.freshdeskArticleIdsText ?? '').toString();
    const ids = this.parseIds(text);

    // keep UI in sync with normalized result (deduped, cleaned)
    e.data.freshdeskArticleIds = ids;
    e.data.freshdeskArticleIdsText = ids.join(', ');

    // send only what backend expects (no UI-only field)
    const payload: SettingTemplateArticle = {
      ...e.data,
      freshdeskArticleIds: ids
    };

    // If your service is strict, strip the UI field:
    // delete (payload as any).freshdeskArticleIdsText;

    this.service.updateArticle(payload).subscribe({
      next: () => Swal.fire({ icon: 'success', text: 'Updated successfully.' }),
      error: showErrorSwal
    });
  }

  /** Refresh grid data */
  refresh() {
    this.loadArticles();
    Swal.fire({ icon: 'success', text: 'Articles refreshed.' });
  }
}
